import { times } from "@common/helpers/collections";
import { Bill, BillSource, BillStats, BillStatus } from "@common/types/bill";
import { Counterparty } from "@common/types/counterparty";
import {
    FinancialAccount,
    FinancialAccountSourceType,
} from "@common/types/financialAccount";
import { isRampReimbursementAccount } from "@common/helpers/rampAccount";
import { getBillExternalUrl } from "@common/helpers/domains/bills/billExternalUrl";
import { getBillcomBaseUrl } from "./billcom";

const RAMP_BILLS_BASE_URL = "https://app.ramp.com/bill-pay/bills";

/**
 * An account that can act as a bill provider (and thus surface bills). Ramp
 * qualifies unless it is a reimbursement-only connection; Bill.com always
 * qualifies. Shared by the page gate and the in-table connect empty state so
 * "connected" means the same thing everywhere.
 */
export const isBillProviderAccount = (account: FinancialAccount): boolean => {
    if (account.sourceType === FinancialAccountSourceType.RAMP) {
        return !account.integrationAccounts?.some(isRampReimbursementAccount);
    }
    return account.sourceType === FinancialAccountSourceType.BILLCOM;
};

export function getBillcomNewBillUrl() {
    return `${getBillcomBaseUrl()}/neo/bills/create`;
}

export const mockBillStats: BillStats = {
    draft: {
        count: 10,
        total: 500,
    },
    open: {
        count: 10,
        total: 2000,
    },
    paid: {
        count: 10,
        total: 1000,
    },
    void: {
        count: 0,
        total: 0,
    },
};

export function getBillUrl(bill: Bill) {
    return (
        getBillExternalUrl({
            bill,
            billcomBaseUrl: getBillcomBaseUrl(),
        }) || undefined
    );
}

export function getBillUrlTitle(bill: Bill) {
    switch (bill.source) {
        case BillSource.RAMP:
            return "View in Ramp";
        case BillSource.BILLCOM:
            return "View in BILL";
        default:
            return "";
    }
}

export function getRampUrlForInvoiceCreation() {
    return `${RAMP_BILLS_BASE_URL}/new`;
}

export function generateMockBills(num: number): Bill[] {
    const statusValues = Object.values(BillStatus);
    return times(num, (i) => {
        const status =
            statusValues[Math.floor(Math.random() * statusValues.length)];

        return {
            id: `bill_${i}`,
            createdAt: new Date(),
            updatedAt: new Date(),
            paidAt: new Date(),
            due: new Date(),
            dateOnly: null,
            paidDateOnly: null,
            dueDateOnly: null,
            status,
            externalStatus: status,
            counterparty: {
                name: "John Doe",
                email: "john@doe.com",
            } as Counterparty,
            total: 1000,
            subtotal: 1000,
            tax: 0,
            discount: 0,
            deposit: null,
            currency: "USD",
            description: `Bill ${i}`,
            memo: null,
            source: BillSource.RAMP,
            sourceId: `bill_${i}`,
            entityId: 1,
            financialAccountId: 0,
            invoiceNumber: `INV-${i}`,
            financialDocumentIds: [],
        };
    });
}
